import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  extractDriveLinks,
  getEvidenceImageUrl,
  parseTicketEvidenceUrls,
} from './supportTicketUtils';

const openEvidenceInNewTab = async (url) => {
  try {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) {
      throw new Error('Could not load image');
    }
    const blob = await response.blob();
    const type = String(blob.type || '').toLowerCase();
    if (type.includes('text/html') || type.includes('application/json')) {
      throw new Error('Image is not available');
    }
    const objectUrl = URL.createObjectURL(blob);
    const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120000);
  } catch (error) {
    toast.error(error?.message || 'Could not open image');
  }
};

const TicketEvidenceViewer = ({
  description = '',
  evidenceUrl = null,
  evidenceUrls = null,
  title = 'Evidence',
}) => {
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const driveLinks = extractDriveLinks(description);
  const imageUrls = useMemo(() => {
    const paths =
      Array.isArray(evidenceUrls) && evidenceUrls.length > 0
        ? evidenceUrls
        : parseTicketEvidenceUrls(evidenceUrl);
    return paths.map(getEvidenceImageUrl).filter(Boolean);
  }, [evidenceUrl, evidenceUrls]);

  useEffect(() => {
    if (!lightboxUrl) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setLightboxUrl(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxUrl]);

  if (!driveLinks.length && !imageUrls.length) {
    return null;
  }

  return (
    <div className="ticket-evidence-viewer">
      <h4>{title}</h4>

      {driveLinks.length > 0 && (
        <div className="ticket-evidence-drive-links">
          <p className="ticket-evidence-label">Google Drive links</p>
          <ul>
            {driveLinks.map((link) => (
              <li key={link}>
                <a href={link} target="_blank" rel="noopener noreferrer">
                  {link}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {imageUrls.length > 0 && (
        <div className="ticket-evidence-images">
          <p className="ticket-evidence-label">
            Uploaded image{imageUrls.length > 1 ? 's' : ''} (click to view)
          </p>
          <div className="ticket-evidence-gallery">
            {imageUrls.map((url) => (
              <button
                key={url}
                type="button"
                className="ticket-evidence-gallery-btn"
                onClick={() => setLightboxUrl(url)}
              >
                <img src={url} alt="Ticket evidence" className="ticket-evidence-image" />
              </button>
            ))}
          </div>
        </div>
      )}

      {lightboxUrl && (
        <div
          className="ticket-evidence-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Evidence image preview"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            type="button"
            className="ticket-evidence-lightbox-close"
            aria-label="Close preview"
            onClick={() => setLightboxUrl(null)}
          >
            ×
          </button>
          <img
            src={lightboxUrl}
            alt="Ticket evidence full size"
            className="ticket-evidence-lightbox-image"
            onClick={(event) => event.stopPropagation()}
          />
          <a
            href={lightboxUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ticket-evidence-lightbox-open"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              openEvidenceInNewTab(lightboxUrl);
            }}
          >
            Open in new tab
          </a>
        </div>
      )}
    </div>
  );
};

export default TicketEvidenceViewer;
